import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button, Card, Spin, Typography, Space, message } from "antd";
import {
  CreditCardOutlined,
  LoadingOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import "./styles.css";
import { API } from "../../Config/API";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { fetchUserProfile } from "../../store/authSlice";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const BRAND = "#0576b7";

const Payment = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isVerifying, setIsVerifying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showRetryButton, setShowRetryButton] = useState(false);
  const [hasCallbackParams, setHasCallbackParams] = useState<boolean | null>(
    null
  );
  const transactionRef = useAppSelector((state) => state.paymentDetails);
  // Auth token comes from redux (rehydrated by redux-persist on app boot
  // under the `persist:auth` key — there is no separate localStorage.authToken).
  const authToken = useAppSelector((state) => state.auth.token);

  // Ref to track if verification has been initiated to prevent duplicate calls
  const verificationInitiated = useRef<string | null>(null);
  const isVerifyingRef = useRef<boolean>(false);

  const MAX_RETRY_ATTEMPTS = 3;
  const RETRY_DELAY = 2000;

  const verifyPayment = useCallback(
    async (tranRef: any, attempt: number = 1) => {
      if (!tranRef) {
        console.error("No transaction reference provided for verification");
        message.error("Missing transaction reference");
        return;
      }

      if (
        attempt === 1 &&
        verificationInitiated.current === tranRef &&
        isVerifyingRef.current
      ) {
        return;
      }

      try {
        if (attempt === 1) {
          verificationInitiated.current = tranRef;
          isVerifyingRef.current = true;
        }

        setIsVerifying(true);
        setRetryCount(attempt - 1);

        const api = API.BASE_URL + "paytabs/verify";
        const response = await fetch(api, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tranRef }),
          signal: AbortSignal.timeout(30000),
        });

        if (!response.ok) {
          throw new Error(
            `HTTP error! status: ${response.status} - ${response.statusText}`
          );
        }

        const result = await response.json();

        if (result?.raw?.payment_result?.response_status === "A") {
          // Existing-user renewal/upgrade flow: PayTabs only verifies that
          // the gateway charged the card. The user's plan choice + new dates
          // were stashed in sessionStorage by RenewalModal — call the
          // completion endpoint to actually create the subscription row and
          // update the user's snapshot. Endpoint is idempotent on tran_ref,
          // so re-entering this screen won't double-create.
          const renewalCtxRaw = sessionStorage.getItem(
            "paytabsRenewalContext"
          );
          if (renewalCtxRaw) {
            try {
              const renewalCtx = JSON.parse(renewalCtxRaw);
              const completeRes = await fetch(
                `${API.BASE_URL}epaper-users/me/paytabs-renewal/complete`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    ...(authToken
                      ? { Authorization: `Bearer ${authToken}` }
                      : {}),
                  },
                  body: JSON.stringify({ data: renewalCtx }),
                }
              );
              const completeJson = await completeRes
                .json()
                .catch(() => ({}));
              if (!completeRes.ok) {
                throw new Error(
                  completeJson?.error?.message ||
                    "Renewal completion failed"
                );
              }
              sessionStorage.removeItem("paytabsRenewalContext");
              if (authToken) {
                try {
                  await (dispatch as any)(
                    fetchUserProfile(authToken)
                  ).unwrap();
                } catch (refreshErr) {
                  console.warn(
                    "Renewal completed but profile refresh failed:",
                    refreshErr
                  );
                }
              }
            } catch (completionErr: any) {
              console.error(
                "Renewal completion failed after successful payment:",
                completionErr
              );
              message.error(
                "Payment confirmed, but we couldn't finalize your subscription. " +
                  "Please refresh the Settings page or contact support."
              );
            }
          }

          isVerifyingRef.current = false;
          sessionStorage.removeItem("paymentTransactionRef");
          navigate("/payment/result/success");
        } else if (result?.raw?.payment_result?.response_status === "D") {
          isVerifyingRef.current = false;
          navigate("/payment/result/failed");
        } else if (result?.raw?.payment_result?.response_status === "P") {
          if (attempt < MAX_RETRY_ATTEMPTS) {
            setTimeout(() => {
              verifyPayment(tranRef, attempt + 1);
            }, RETRY_DELAY);
          } else {
            setIsVerifying(false);
            isVerifyingRef.current = false;
            setShowRetryButton(true);
            message.error(
              "Payment verification timed out. You can retry or contact support if payment was debited."
            );
          }
        } else {
          isVerifyingRef.current = false;
          navigate("/payment/result/failed");
        }

        return result;
      } catch (err: any) {
        if (attempt < MAX_RETRY_ATTEMPTS && !err.name?.includes("AbortError")) {
          message.warning(
            `Verification failed, retrying... (${attempt}/${MAX_RETRY_ATTEMPTS})`
          );
          setTimeout(() => {
            verifyPayment(tranRef, attempt + 1);
          }, RETRY_DELAY);
        } else {
          setIsVerifying(false);
          isVerifyingRef.current = false;
          setShowRetryButton(true);
          if (err.name?.includes("AbortError")) {
            message.error(
              "Payment verification timed out. You can retry manually."
            );
          } else {
            message.error(
              "Failed to verify payment after multiple attempts. You can retry or contact support."
            );
          }
        }
      }
    },
    [navigate, authToken, dispatch]
  );

  const handleManualRetry = () => {
    const sessionTranRef = sessionStorage.getItem("paymentTransactionRef");
    const tranRefToUse = transactionRef?.paymentData || sessionTranRef;

    if (tranRefToUse) {
      setShowRetryButton(false);
      setRetryCount(0);
      verificationInitiated.current = null;
      isVerifyingRef.current = false;
      verifyPayment(tranRefToUse);
    } else {
      message.error(
        "No transaction reference found. Please start a new payment."
      );
    }
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const sessionTranRef = sessionStorage.getItem("paymentTransactionRef");

    const typeParam = params.get("type");
    const tranRef =
      params.get("tran_ref") ||
      params.get("transaction_ref") ||
      params.get("tranRef");
    const paymentStatus = params.get("payment_status") || params.get("status");
    const responseStatus = params.get("response_status");
    const cartId = params.get("cart_id");

    let shouldVerifyPayment = false;
    let transactionRefToUse: string | null = null;

    if (typeParam === "paid") {
      shouldVerifyPayment = true;
      transactionRefToUse = transactionRef?.paymentData || sessionTranRef;
    } else if (tranRef) {
      shouldVerifyPayment = true;
      transactionRefToUse = tranRef;
    } else if (
      paymentStatus &&
      (paymentStatus.toLowerCase() === "success" ||
        paymentStatus.toLowerCase() === "completed")
    ) {
      shouldVerifyPayment = true;
      transactionRefToUse =
        tranRef || transactionRef?.paymentData || sessionTranRef;
    } else if (responseStatus === "A") {
      shouldVerifyPayment = true;
      transactionRefToUse =
        tranRef || transactionRef?.paymentData || sessionTranRef;
    } else if (cartId) {
      shouldVerifyPayment = true;
      transactionRefToUse =
        tranRef || transactionRef?.paymentData || sessionTranRef;
    }

    setHasCallbackParams(shouldVerifyPayment);

    if (shouldVerifyPayment && transactionRefToUse) {
      if (
        verificationInitiated.current === transactionRefToUse &&
        isVerifyingRef.current
      ) {
        return;
      }
      verifyPayment(transactionRefToUse);
    } else if (shouldVerifyPayment && !transactionRefToUse) {
      console.error(
        "Payment verification triggered but no transaction reference found"
      );
      message.error(
        "Payment verification failed: Missing transaction reference"
      );
    }
  }, [transactionRef, verifyPayment]);

  // Direct visit (no PayTabs callback params) — nothing to do here.
  // Send the user back to home so they can start a flow from the right place.
  useEffect(() => {
    if (hasCallbackParams === false) {
      navigate("/", { replace: true });
    }
  }, [hasCallbackParams, navigate]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #e8f4fb 0%, #0576b7 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <Card
        style={{
          width: "100%",
          maxWidth: 460,
          borderRadius: 20,
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
          border: "none",
          background: "rgba(255, 255, 255, 0.95)",
        }}
        bodyStyle={{ padding: 40, textAlign: "center" }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Spin
            indicator={
              <LoadingOutlined style={{ fontSize: 48, color: BRAND }} spin />
            }
            size="large"
          />
          <Title
            level={4}
            style={{
              margin: 0,
              color: "#222",
              fontFamily: "NotoSansMalayalam-Bold",
            }}
          >
            {retryCount > 0
              ? `Verifying Payment (${retryCount + 1}/${MAX_RETRY_ATTEMPTS})`
              : "Processing Payment"}
          </Title>
          <Text style={{ color: "#666" }}>
            Please wait while we securely verify your payment. Do not close or
            refresh this window.
          </Text>

          {showRetryButton && (
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                type="primary"
                size="large"
                block
                icon={<CreditCardOutlined />}
                loading={isVerifying}
                onClick={handleManualRetry}
                style={{
                  height: 48,
                  background: BRAND,
                  borderColor: BRAND,
                  fontFamily: "NotoSansMalayalam-SemiBold",
                }}
              >
                Retry Verification
              </Button>
              <Button
                size="large"
                block
                icon={<HomeOutlined />}
                onClick={() => navigate("/")}
                style={{
                  height: 48,
                  fontFamily: "NotoSansMalayalam-SemiBold",
                }}
              >
                Go to Home
              </Button>
            </Space>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default Payment;
